'use client';

import useSWR from 'swr';

import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { homeApi } from '@/lib/api';
import { calendarScopeQuery } from '@/lib/calendarScope';
import type { OpenInvoicesWidget } from '@/types';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

/**
 * W3 · Em aberto — open credit-card invoices for the current cycle from
 * `GET /api/home/open-invoices` (total + per-card totals + a 5-month
 * past/current/forecast evolution). The key rides the `home-widget` prefix
 * (FINANCIAL_DATA_KEY_PREFIXES) so the existing mutation/sync revalidation
 * sweeps refresh this widget too.
 *
 * The cycle is the CLIENT's calendar month (`calendarScopeQuery`), not the server's UTC
 * month; the key carries it so an open tab refetches at the local turn of the month.
 */
export function useOpenInvoicesWidget(): {
  data: OpenInvoicesWidget | undefined;
  error: unknown;
  isLoading: boolean;
} {
  const authedFetch = useAuthedFetcher();
  const { year, month } = useCurrentMonthScope();

  const { data, error, isLoading } = useSWR<OpenInvoicesWidget>(
    ['home-widget-open-invoices', year, month],
    () => authedFetch((token) => homeApi.getOpenInvoices(token, calendarScopeQuery())),
    LONG_LIVED_SWR_OPTIONS,
  );

  return { data, error, isLoading };
}
